import {
  EnvironmentInjector,
  Injectable,
  inject,
  runInInjectionContext
} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  PRIMARY_OUTLET,
  Params,
  Router
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  Observable,
  catchError,
  combineLatest,
  defer,
  filter,
  map,
  of,
  shareReplay,
  startWith,
  switchMap
} from 'rxjs';

import { humanizeIdentifier } from '@gfe/core/utils/case-conversions';
import { isNil, isString } from '@gfe/core/utils/shared-utils';
import { AvailableLanguages } from '@gfe/shared/types/translation.type';

import {
  BREADCRUMB_DATA_KEY,
  BreadcrumbItem,
  CrumbContext,
  CrumbDef,
  CrumbLabelValue,
  RouteBreadcrumb
} from './breadcrumb.model';

interface MatchedCrumb {
  def: CrumbDef;
  context: CrumbContext;
  url: string;
}

/**
 * Builds the breadcrumb from the `data.breadcrumb` each matched route declares
 * (see `RouteBreadcrumb`).
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(EnvironmentInjector);

  // Shared so the trail, the page title and the assistant resolve each label once.
  private readonly breadcrumbs$: Observable<BreadcrumbItem[]> = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    startWith(null),
    map(() => matchCrumbs(this.router.routerState.snapshot.root)),
    switchMap((matched) => this.resolve(matched)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  getBreadcrumbs(): Observable<BreadcrumbItem[]> {
    return this.breadcrumbs$;
  }

  private resolve(matched: MatchedCrumb[]): Observable<BreadcrumbItem[]> {
    if (matched.length === 0) {
      return of([]);
    }
    const lastIndex = matched.length - 1;
    return combineLatest(
      matched.map((crumb, index) =>
        this.label$(crumb).pipe(
          map(
            (label): BreadcrumbItem => ({
              label,
              link: crumb.def.link && index < lastIndex ? crumb.url : null
            })
          )
        )
      )
    );
  }

  private label$({ def, context }: MatchedCrumb): Observable<string> {
    const lang$ = this.translate.onLangChange.pipe(startWith(null));
    const { label } = def;

    if (isString(label)) {
      return lang$.pipe(map(() => this.translate.instant(label) as string));
    }

    // Deferred so a label that throws while being built falls back like one that errors later,
    // instead of erroring the trail every subscriber shares.
    const value$ = defer(() =>
      runInInjectionContext(this.injector, () => label(context))
    ).pipe(
      startWith(undefined),
      catchError(() => of(undefined))
    );
    return combineLatest([value$, lang$]).pipe(
      map(([value]) => this.text(value, context.segment))
    );
  }

  private text(value: CrumbLabelValue, segment: string): string {
    if (isString(value)) {
      return value || humanizeIdentifier(segment);
    }
    const lang = this.translate.getCurrentLang() as AvailableLanguages;
    return value?.[lang] || value?.en || humanizeIdentifier(segment);
  }
}

function matchCrumbs(root: ActivatedRouteSnapshot): MatchedCrumb[] {
  const matched: MatchedCrumb[] = [];
  const segments: string[] = [];
  let params: Params = {};

  for (
    let route: ActivatedRouteSnapshot | undefined = root;
    route;
    route = route.children.find((child) => child.outlet === PRIMARY_OUTLET)
  ) {
    params = { ...params, ...route.params };
    const routeSegments = route.url.map((segment) => segment.path);
    const declared = route.routeConfig?.data?.[BREADCRUMB_DATA_KEY] as
      | RouteBreadcrumb
      | undefined;

    // An empty-path route (a dataspace's home page) still gets the crumb it declares.
    if (routeSegments.length === 0 && isCrumbDef(declared)) {
      matched.push({
        def: declared,
        context: { params, segment: '' },
        url: `/${segments.join('/')}`
      });
      continue;
    }

    const perSegment = expand(declared, routeSegments.length);
    routeSegments.forEach((segment, index) => {
      segments.push(segment);
      const def = perSegment[index];
      if (def) {
        matched.push({ def, context: { params, segment }, url: `/${segments.join('/')}` });
      }
    });
  }
  return matched;
}

function expand(
  declared: RouteBreadcrumb | undefined,
  segmentCount: number
): readonly (CrumbDef | null)[] {
  if (isNil(declared)) {
    return [];
  }
  if (isCrumbDef(declared)) {
    return [...Array<null>(segmentCount - 1).fill(null), declared];
  }
  return declared;
}

function isCrumbDef(declared: RouteBreadcrumb | undefined): declared is CrumbDef {
  return !isNil(declared) && !Array.isArray(declared);
}
